import {v4 as uuid} from 'uuid';
import {api} from '~/common/fabric/EventGuests/api/api';
import {repopulateContacts} from '~/common/fabric/EventGuests/Guests/GuestSearchSuggestions/useContactsWorker';
import {clearGuestListSearchParam} from '~/common/fabric/EventGuests/reducers/guests';

export const guestsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    fetchGuests: builder.query({
      async queryFn({eventId, searchParams, inEditMode}, _queryApi, _extraOptions, baseQuery) {
        if (!eventId) {
          return {
            data: {
              guests: [],
              sent: [],
              totalDrafts: 0,
              totalSent: 0,
            },
          };
        }

        const params = new URLSearchParams(searchParams).toString();
        const sentUrl = `/event/${eventId}/guestlist/sent/?${params}`;
        let draftUrl = `/event/${eventId}/guestlist/draft/?${params}`;

        if (inEditMode) {
          draftUrl = `/event/${eventId}/guestlist/all/?${params}`;
        }

        const [draftResponse, sentResponse] = await Promise.allSettled([
          baseQuery({url: draftUrl}),
          baseQuery({url: sentUrl}),
        ]);

        const result = {
          guests: draftResponse.value.data?.guests || [],
          sent: sentResponse.value.data?.guests || [],
          totalDrafts: draftResponse.value.data?.total_drafts || 0,
          totalSent: draftResponse.value.data?.total_sent || 0,
        };

        return {data: result};
      },
      providesTags: [{type: 'Guests', id: `LIST`}],
    }),

    deleteGuest: builder.mutation({
      query: ({eventId, guestId}) => ({
        url: `/event/${eventId}/guestlist/draft/${guestId}`,
        method: 'DELETE',
      }),
      async onQueryStarted({guestId}, {dispatch, queryFulfilled, getState}) {
        const args = api.util.selectCachedArgsForQuery(getState(), 'fetchContacts');
        const guestsArgs = api.util.selectCachedArgsForQuery(getState(), 'fetchGuests');
        const patchResult = dispatch(
          api.util.updateQueryData('fetchContacts', args[args.length - 1], (draft) => ({
            ...draft,
            contacts: {
              ...draft.contacts,
              page: draft.contacts.page.map((contact) => {
                if (guestId === contact.guest_id) {
                  return {...contact, selected: false};
                }
                return contact;
              }),
            },
          }))
        );

        try {
          await queryFulfilled;

          const lastArgs = guestsArgs[guestsArgs.length - 1];
          const cachedData = api.endpoints.fetchGuests.select(lastArgs)(getState());
          if (cachedData?.data?.guests?.page?.length === 1 && cachedData.data.guests?.count > 0) {
            const {eventId, searchParams} = lastArgs;
            const currentPage = parseInt(searchParams.page || '1', 10);
            if (currentPage > 1) {
              const previousPageParams = new URLSearchParams(searchParams);
              previousPageParams.set('page', (currentPage - 1).toString());

              dispatch(
                api.endpoints.fetchGuests.initiate({
                  eventId,
                  searchParams: previousPageParams,
                  inEditMode: lastArgs.inEditMode,
                })
              );
            } else if (searchParams.q) {
              dispatch(clearGuestListSearchParam());
            }
          }
        } catch (_error) {
          patchResult.undo(); // Rollback if the mutation fails
        }
      },
      invalidatesTags: () => [{type: 'Guests', id: 'LIST'}],
    }),

    deleteGuestOptimistic: builder.mutation({
      query: ({eventId, guestId}) => ({
        url: `/event/${eventId}/guestlist/draft/${guestId}`,
        method: 'DELETE',
      }),
      async onQueryStarted({guestId, eventId}, {dispatch, queryFulfilled, getState}) {
        const guestsArgs = api.util.selectCachedArgsForQuery(getState(), 'fetchGuests');
        const lastArgs = guestsArgs[guestsArgs.length - 1];
        const patchResult = dispatch(
          api.util.updateQueryData('fetchGuests', lastArgs, (draft) => ({
            ...draft,
            ...{
              guests: {
                ...draft.guests,
                ...{
                  page: draft.guests.page.filter((g) => g.guest_id !== guestId),
                },
              },
            },
          }))
        );
        try {
          await queryFulfilled;
          window.setTimeout(() => repopulateContacts(eventId), 400);
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: () => [{type: 'Guests', id: 'LIST'}],
    }),

    deleteContacts: builder.mutation({
      query: ({eventId}) => ({
        url: `/event/${eventId}/guestlist/contacts/group-delete/draft/`,
        method: 'POST',
        credentials: 'include',
      }),
      invalidatesTags: [
        {type: 'Contacts', id: 'LIST'},
        {type: 'Guests', id: 'LIST'},
      ],
    }),
    deleteImports: builder.mutation({
      query: ({eventId}) => ({
        url: `/event/${eventId}/guestlist/import/group-delete/draft/`,
        method: 'POST',
        credentials: 'include',
      }),
      invalidatesTags: [
        {type: 'Contacts', id: 'LIST'},
        {type: 'Guests', id: 'LIST'},
      ],
    }),
    deleteAllDraftContacts: builder.mutation({
      query: ({eventId}) => ({
        url: `/event/${eventId}/guestlist/draft/all`,
        method: 'DELETE',
        credentials: 'include',
      }),
      invalidatesTags: [
        {type: 'Contacts', id: 'LIST'},
        {type: 'Guests', id: 'LIST'},
      ],
    }),

    updateGuest: builder.mutation({
      query: ({eventId, guestData}) => ({
        url: `/event/${eventId}/guestlist/draft/`,
        method: 'PATCH',
        body: guestData,
      }),
    }),

    addNewGuests: builder.mutation({
      query: ({eventId, guests}) => ({
        url: `/event/${eventId}/guestlist/draft/`,
        method: 'POST',
        body: JSON.stringify(guests),
      }),
      async onQueryStarted({guests}, {dispatch, queryFulfilled, getState}) {
        const args = api.util.selectCachedArgsForQuery(getState(), 'fetchContacts');
        const lastArg = args[args.length - 1];
        const {data} = await queryFulfilled;
        const [guest] = data?.guests || [];
        dispatch(
          api.util.updateQueryData('fetchContacts', lastArg, (draft) => ({
            ...draft,
            contacts: {
              ...draft.contacts,
              page: draft.contacts.page.map((contact) => {
                if (guests.some((_) => _.id === contact.id)) {
                  // biome-ignore lint/style/useNamingConvention: camel case>
                  return {...contact, selected: true, guest_id: guest?.guest_id};
                }
                return contact;
              }),
            },
          }))
        );
        if (!guest) {
          dispatch(api.util.invalidateTags([{type: 'Contacts', id: 'LIST'}]));
        }
      },
      invalidatesTags: [{type: 'Guests', id: 'LIST'}],
    }),

    addNewGuestsOptimistic: builder.mutation({
      query: ({eventId, guests}) => ({
        url: `/event/${eventId}/guestlist/draft/`,
        method: 'POST',
        body: JSON.stringify(guests),
      }),
      async onQueryStarted({guests}, {dispatch, queryFulfilled, getState}) {
        const args = api.util.selectCachedArgsForQuery(getState(), 'fetchGuests');
        const lastArg = args[args.length - 1];
        const {data} = await queryFulfilled;
        const [guest] = data?.guests || [];
        const patchResult = dispatch(
          api.util.updateQueryData('fetchGuests', lastArg, (draft) => ({
            ...draft,
            guests: {
              ...draft.guests,
              page: [
                ...draft.guests.page,
                ...guests.map((g) => ({
                  ...g,
                  draft: true,
                  // biome-ignore lint/style/useNamingConvention: camel case
                  guest_id: uuid(),
                })),
              ],
            },
          }))
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
        dispatch(
          api.util.updateQueryData('fetchGuests', lastArg, (draft) => ({
            ...draft,
          }))
        );
        if (!guest) {
          dispatch(api.util.invalidateTags([{type: 'Contacts', id: 'LIST'}]));
        }
      },
      invalidatesTags: [{type: 'Guests', id: 'LIST'}],
    }),
  }),
});

export const {
  useFetchGuestsQuery,
  useLazyFetchGuestsQuery,
  useDeleteGuestOptimisticMutation,
  useUpdateGuestMutation,
  useAddNewGuestsOptimisticMutation,
} = guestsApi;
